import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import WorkoutChart from './WorkoutChart';
import WorkoutShareModal from './WorkoutShareModal';
import WorkoutSharedBy from './WorkoutSharedBy';
import ActivityDetails from './ActivityDetails';
import {
  ZONES,
  formatDuration,
  formatPercent,
  formatWatts,
  groupSegments,
  hasPower,
  zoneDistribution,
  zonePieces,
  zoneRange,
  workoutMetrics,
} from '../utils/workouts';
import { buildFolderTree, folderOptions, folderPath, folderUrl } from '../utils/workoutFolders';
import { SPORTS, formatDateTime } from '../utils/activities';
import { downloadFile, readBlobError } from '../utils/fileDownload';
import './WorkoutsPage.css';
import './WorkoutView.css';

// Иконка share-2 из Feather Icons (MIT): макета для нее нет, в проекте своей тоже
const ShareIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const WorkoutView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();

  const [workout, setWorkout] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [workoutRes, foldersRes] = await Promise.all([
          axios.get(`/api/workouts/${id}`),
          axios.get('/api/workout_folders').catch(() => ({ data: { folders: [] } })),
        ]);
        setWorkout(workoutRes.data);
        setFolders(foldersRes.data.folders);
      } catch (err) {
        setError(err.response?.data?.error || t('workouts.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, t]);

  const segments = useMemo(() => workout?.segments || [], [workout]);
  const metrics = useMemo(() => workoutMetrics(segments), [segments]);
  const distribution = useMemo(() => zoneDistribution(segments), [segments]);
  const rows = useMemo(() => groupSegments(segments), [segments]);
  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  if (loading) return <div className="workouts-page"><p className="workouts-muted">{t('common.loading')}</p></div>;
  if (error || !workout) {
    return (
      <div className="workouts-page">
        <Link to={folderUrl(null)} className="workout-back">← {t('workouts.title')}</Link>
        <p className="workouts-errors">{error}</p>
      </div>
    );
  }

  // Ватты считаем от FTP профиля, а если его нет — от FTP, с которым пересчитан ERG
  const ftp = user?.ftp || workout.ftp || null;
  const ftpNote = user?.ftp
    ? t('workouts.ftpFromProfile').replace('{{ftp}}', user.ftp)
    : workout.ftp
      ? t('workouts.ftpFromFile').replace('{{ftp}}', workout.ftp)
      : t('workouts.ftpMissing');

  const runAction = async (action) => {
    setBusy(true);
    setMessage('');
    setActionError('');
    try {
      await action();
    } catch (err) {
      setActionError(err.response?.data?.error || t('workouts.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const saveName = () => runAction(async () => {
    const res = await axios.patch(`/api/workouts/${workout.id}`, { name: nameDraft.trim() });
    setWorkout(res.data);
    setEditingName(false);
  });

  // Вид тренировки, если бэкенд не угадал; пустое значение — «не указан»
  const changeSport = (value) => runAction(async () => {
    const res = await axios.patch(`/api/workouts/${workout.id}`, { sport: value });
    setWorkout(res.data);
  });

  // Перенос в другую папку — для телефонов, где перетаскивания в списке нет
  const moveToFolder = (value) => runAction(async () => {
    const res = await axios.patch(`/api/workouts/${workout.id}`, { folder_id: value ? Number(value) : null });
    setWorkout(res.data);
    setMessage(t('workouts.folders.moved'));
  });

  const deleteWorkout = () => {
    if (!window.confirm(t('workouts.deleteConfirm'))) return;
    runAction(async () => {
      await axios.delete(`/api/workouts/${workout.id}`);
      navigate(folderUrl(workout.folder_id));
    });
  };

  // as: 'zwo' — для ROUVY/Zwift, 'gpx' — только трек заезда, без него — исходник
  const download = (as) => runAction(async () => {
    try {
      await downloadFile(`/api/workouts/${workout.id}/download`, {
        params: as ? { as } : undefined,
        fallbackName: as ? `workout.${as}` : workout.original_filename,
      });
    } catch (err) {
      throw Object.assign(err, { response: { data: { error: await readBlobError(err) } } });
    }
  });

  const powerText = (segment) => {
    if (segment.kind === 'free') return t('workouts.kind.free');
    if (segment.kind === 'max') return t('workouts.kind.max');
    if (segment.kind === 'ramp') {
      const watts = ftp ? ` (${formatWatts(segment.power_low, ftp)}→${formatWatts(segment.power_high, ftp)} ${t('workouts.units.watts')})` : '';
      return `${formatPercent(segment.power_low)} → ${formatPercent(segment.power_high)}${watts}`;
    }
    const watts = ftp ? ` (${formatWatts(segment.power_low, ftp)} ${t('workouts.units.watts')})` : '';
    return `${formatPercent(segment.power_low)}${watts}`;
  };

  const zoneCell = (segment) => {
    if (!hasPower(segment)) return '—';
    const zones = [...new Set(zonePieces(segment).map((piece) => piece.zone))];
    return (
      <span className="workout-zone-list">
        {zones.map((zone) => (
          <span key={zone.id} className="workout-zone-chip">
            <span className="workout-zone-swatch" style={{ background: zone.color }} />
            Z{zone.id}
          </span>
        ))}
      </span>
    );
  };

  const segmentRow = ({ segment, index }, nested = false) => (
    <tr key={index} className={nested ? 'workout-row-nested' : undefined}>
      <td className="workout-cell-num">{index + 1}</td>
      <td className="workout-cell-num">{formatDuration(segment.start)}</td>
      <td className="workout-cell-num">{formatDuration(segment.duration)}</td>
      <td>{powerText(segment)}</td>
      <td>{zoneCell(segment)}</td>
      <td className="workout-cell-num">{segment.cadence || '—'}</td>
    </tr>
  );

  const maxZoneSeconds = Math.max(...distribution.zones.map((item) => item.seconds), distribution.free, 1);
  const totalSeconds = Math.max(workout.duration_seconds, 1);
  // Ненулевое время не должно выглядеть как «0%»
  const shareText = (seconds) => {
    const share = (seconds / totalSeconds) * 100;
    return seconds > 0 && share < 0.5 ? '<1%' : `${Math.round(share)}%`;
  };

  const tiles = [
    { label: t('workouts.metrics.duration'), value: formatDuration(workout.duration_seconds) },
    metrics.avg !== null && {
      label: t('workouts.metrics.avg'),
      value: formatPercent(metrics.avg),
      sub: ftp ? `${formatWatts(metrics.avg, ftp)} ${t('workouts.units.watts')}` : null,
    },
    metrics.np !== null && {
      label: t('workouts.metrics.np'),
      value: formatPercent(metrics.np),
      sub: ftp ? `${formatWatts(metrics.np, ftp)} ${t('workouts.units.watts')}` : null,
    },
    metrics.intensity !== null && { label: 'IF', value: metrics.intensity.toFixed(2) },
    metrics.tss !== null && { label: 'TSS', value: Math.round(metrics.tss) },
    metrics.avg !== null && ftp && {
      label: t('workouts.metrics.work'),
      value: `${Math.round((metrics.avg * ftp * metrics.counted) / 1000)} ${t('workouts.units.kj')}`,
    },
  ].filter(Boolean);

  const path = folderPath(tree, workout.folder_id);
  // Выполненная тренировка или маршрут (GPX/FIT/TCX) вместо профиля плана
  const isActivity = workout.kind === 'activity';
  const hasTrack = isActivity && workout.track?.length > 1;

  return (
    <div className="workouts-page workout-view">
      <nav className="wf-breadcrumbs workout-view-path" aria-label={t('workouts.folders.path')}>
        <Link to={folderUrl(null)} className="wf-breadcrumbs-link">← {t('workouts.folders.root')}</Link>
        {path.map((folder) => (
          <React.Fragment key={folder.id}>
            <span className="wf-breadcrumbs-sep">/</span>
            <Link to={folderUrl(folder.id)} className="wf-breadcrumbs-link">{folder.name}</Link>
          </React.Fragment>
        ))}
      </nav>

      <div className="workout-view-head">
        <div className="workout-view-title-row">
          {editingName ? (
            <div className="workout-name-edit">
              <input
                className="workouts-input workout-name-input"
                value={nameDraft}
                maxLength={200}
                onChange={(event) => setNameDraft(event.target.value)}
                autoFocus
              />
              <button type="button" className="workouts-primary-btn" onClick={saveName} disabled={busy || !nameDraft.trim()}>
                {t('common.save')}
              </button>
              <button type="button" className="workouts-secondary-btn" onClick={() => setEditingName(false)} disabled={busy}>
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <h2>
              {workout.name}
              <button
                type="button"
                className="workout-rename-btn"
                onClick={() => { setNameDraft(workout.name); setEditingName(true); }}
                title={t('workouts.rename')}
                aria-label={t('workouts.rename')}
              >
                ✎
              </button>
            </h2>
          )}
          <button
            type="button"
            className="workout-share-btn"
            onClick={() => setShowShare(true)}
            title={t('workouts.share.button')}
            aria-label={t('workouts.share.button')}
          >
            <ShareIcon />
          </button>
        </div>
        {isActivity ? (
          <p className="workouts-muted">
            {[
              formatDateTime(workout.started_at, language),
              `${workout.source_format.toUpperCase()} · ${workout.original_filename}`,
              workout.author,
            ].filter(Boolean).join(' · ')}
          </p>
        ) : (
          <p className="workouts-muted">
            {workout.source_format.toUpperCase()} · {workout.original_filename}
            {workout.author ? ` · ${workout.author}` : ''}
          </p>
        )}
        <WorkoutSharedBy sender={workout.shared_by} className="workout-view-shared" />
        {workout.description && <p className="workout-description">{workout.description}</p>}
      </div>

      {message && <p className="workouts-success">{message}</p>}
      {actionError && <p className="workouts-errors">{actionError}</p>}

      {isActivity ? <ActivityDetails workout={workout} /> : (
        <>
          <section className="workout-card-block">
            <WorkoutChart
              segments={segments}
              duration={workout.duration_seconds}
              ftp={ftp}
              textEvents={workout.text_events}
            />
            <div className="workout-legend" aria-label={t('workouts.zonesTitle')}>
              {ZONES.map((zone) => (
                <span key={zone.id} className="workout-zone-chip">
                  <span className="workout-zone-swatch" style={{ background: zone.color }} />
                  Z{zone.id}
                </span>
              ))}
              {distribution.free > 0 && (
                <span className="workout-zone-chip">
                  <span className="workout-zone-swatch workout-zone-swatch--free" />
                  {t('workouts.kind.noTarget')}
                </span>
              )}
            </div>
          </section>

          <section className="workout-tiles">
            {tiles.map((tile) => (
              <div key={tile.label} className="workout-tile">
                <span className="workout-tile-label">{tile.label}</span>
                <span className="workout-tile-value">{tile.value}</span>
                {tile.sub && <span className="workout-tile-sub">{tile.sub}</span>}
              </div>
            ))}
          </section>
          <p className="workouts-muted workout-ftp-note">{ftpNote}</p>

          <section className="workout-card-block">
            <h3>{t('workouts.zonesTitle')}</h3>
            <div className="workout-zones">
              {distribution.zones.map(({ zone, seconds }) => (
                <div key={zone.id} className={`workout-zone-row ${seconds === 0 ? 'workout-zone-row--empty' : ''}`}>
                  <span className="workout-zone-name">
                    <span className="workout-zone-swatch" style={{ background: zone.color }} />
                    <strong>Z{zone.id}</strong> {t(`workouts.zones.${zone.key}`)}
                  </span>
                  <span className="workout-zone-range">{zoneRange(zone)}</span>
                  <span className="workout-zone-bar">
                    <span style={{ width: `${(seconds / maxZoneSeconds) * 100}%`, background: zone.color }} />
                  </span>
                  <span className="workout-zone-time">{seconds > 0 ? formatDuration(seconds) : '—'}</span>
                  <span className="workout-zone-share">{shareText(seconds)}</span>
                </div>
              ))}
              {distribution.free > 0 && (
                <div className="workout-zone-row">
                  <span className="workout-zone-name">
                    <span className="workout-zone-swatch workout-zone-swatch--free" />
                    {t('workouts.kind.noTarget')}
                  </span>
                  <span className="workout-zone-range">—</span>
                  <span className="workout-zone-bar">
                    <span className="workout-zone-bar-free" style={{ width: `${(distribution.free / maxZoneSeconds) * 100}%` }} />
                  </span>
                  <span className="workout-zone-time">{formatDuration(distribution.free)}</span>
                  <span className="workout-zone-share">{shareText(distribution.free)}</span>
                </div>
              )}
            </div>
          </section>

          <section className="workout-card-block">
            <h3>{t('workouts.segmentsTitle')}</h3>
            <div className="workout-table-wrap">
              <table className="workout-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('workouts.table.start')}</th>
                    <th>{t('workouts.table.duration')}</th>
                    <th>{t('workouts.table.power')}</th>
                    <th>{t('workouts.table.zone')}</th>
                    <th>{t('workouts.table.cadence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (row.type === 'single'
                    ? segmentRow(row)
                    : (
                      <React.Fragment key={`repeat-${row.start}`}>
                        <tr className="workout-row-repeat">
                          <td colSpan={6}>
                            {t('workouts.repeat')
                              .replace('{{count}}', row.count)
                              .replace('{{duration}}', formatDuration(row.end - row.start))}
                          </td>
                        </tr>
                        {row.items.map((item) => segmentRow(item, true))}
                      </React.Fragment>
                    )))}
                </tbody>
              </table>
            </div>
          </section>

          {workout.text_events.length > 0 && (
            <section className="workout-card-block">
              <h3>{t('workouts.textEventsTitle')}</h3>
              <ul className="workout-events">
                {workout.text_events.map((event, i) => (
                  <li key={i}>
                    <span className="workout-cell-num">{formatDuration(event.offset)}</span> {event.message}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <div className="workout-actions">
        {isActivity && (
          <label className="workout-folder-select">
            <span>{t('workouts.activity.sport')}</span>
            <select
              className="workouts-input"
              value={workout.sport || ''}
              onChange={(event) => changeSport(event.target.value)}
              disabled={busy}
            >
              <option value="">{t('workouts.activity.sportUnknown')}</option>
              {SPORTS.map((sport) => (
                <option key={sport} value={sport}>{t(`workouts.activity.sports.${sport}`)}</option>
              ))}
            </select>
          </label>
        )}
        <label className="workout-folder-select">
          <span>{t('workouts.folders.folder')}</span>
          <select
            className="workouts-input"
            value={workout.folder_id ? String(workout.folder_id) : ''}
            onChange={(event) => moveToFolder(event.target.value)}
            disabled={busy}
          >
            <option value="">{t('workouts.folders.root')}</option>
            {folderOptions(tree).map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        {!isActivity && (
          <button type="button" className="workouts-secondary-btn" onClick={() => download('zwo')} disabled={busy}>
            {t('workouts.downloadZwo')}
          </button>
        )}
        {hasTrack && (
          <button type="button" className="workouts-secondary-btn" onClick={() => download('gpx')} disabled={busy}>
            {t('workouts.downloadTrack')}
          </button>
        )}
        {isActivity && (
          <button type="button" className="workouts-secondary-btn" onClick={() => download()} disabled={busy}>
            {t('workouts.downloadOriginal').replace('{{format}}', workout.source_format.toUpperCase())}
          </button>
        )}
        {!isActivity && workout.source_format !== 'zwo' && (
          <button type="button" className="workouts-secondary-btn" onClick={() => download()} disabled={busy}>
            {t('workouts.downloadOriginal').replace('{{format}}', workout.source_format.toUpperCase())}
          </button>
        )}
        <button type="button" className="workouts-danger-btn" onClick={deleteWorkout} disabled={busy}>
          {t('common.delete')}
        </button>
      </div>

      {showShare && <WorkoutShareModal workout={workout} onClose={() => setShowShare(false)} />}
    </div>
  );
};

export default WorkoutView;
